import React, { useMemo, useEffect, useState, useRef, memo, Fragment } from 'react';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';
import isBetween from 'dayjs/plugin/isBetween';
import _ from 'lodash';

import {
    Text,
    StyledCard,
    StyledCardContent,
    Avatar,
    CheckCircleIcon,
    UnCheckCircleIcon,
    IconButton,
    StyledDialog,
    OutlinedButton,
    ContainedButton,
    Select,
    KeyboardDatePicker,
    Span,
    EditIcon,
    DeleteIcon,
    HorizontalRule,
    PlusIcon,
    CrossIcon,
    CheckIcon,
    StyledFormHelperText,
    Tag,
} from '../index';

import {
    StyledPaper,
    StyledDialogContent,
    StyledDialogActions,
    StyledDuplicatePaper,
    StyledWorkoutGroupContainer,
    StyledWorkoutGroupButton,
    StyledWorkoutContainer,
    StyledWorkoutButtonContainer,
    StyledWorkoutButtonDividerContainer,
    StyledWorkoutButtonDivider,
    StyledWorkoutButton,
    StyledWorkoutDeleteButton,
    StyledSelectedWorkoutContainerButton,
    StyledSelectedWorkoutContainer,
    StyledWorkoutInput,
    StyledSelectContainer,
    StyledShowMoreContainer,
} from './style';

import { processMindgymActivity } from '../../utils/mindgym';
import ShowMore from '../../utils/showMore';

const MindGymIcon = '/resources/images/mindgym/mindgym-icon-big.svg';
const AppStoreBadge = '/resources/images/mindgym/app-store-badge.svg';
const PlayStoreBadge = '/resources/images/mindgym/play-store-badge.svg';

const MINDGYM_APPLE_STORE_URL = process.env.MINDGYM_APPLE_STORE_URL;
const MINDGYM_PLAY_STORE_URL = process.env.MINDGYM_PLAY_STORE_URL;

dayjs.extend(objectSupport);
dayjs.extend(isBetween);

export const ActivityCard = memo(({ data, isSelected, disabled, onClick }) => {
    return (
        <StyledCard
            onClick={disabled ? () => {} : () => onClick()}
            style={{
                backdropFilter: 'blur(2px)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                // filter: disabled ? 'grayscale(1)' : 'none',
            }}
            padding={isSelected ? '13px 17px 13px 13px' : '14px 18px 14px 14px'}
            $hoverPadding={!disabled && '13px 17px 13px 13px'}
            $borderWidth={isSelected ? '2px' : '1px'}
            $borderColor={isSelected ? 'primaryBlue' : 'gray7'}
            $hoverBorderWidth={!disabled && '2px'}
            $hoverBorderColor={!disabled && 'primaryBlue'}
        >
            <StyledCardContent
                style={{
                    display: 'grid',
                    gridTemplateColumns: '64px auto 24px',
                    columnGap: '15px',
                    alignItems: 'center',
                }}
            >
                <Avatar
                    alt={`activity_${data.title}`}
                    src={data.icon}
                    $avatarSize="64px"
                    disabled={disabled}
                    $borderRadius="12px"
                    noBackgroundColor
                    style={{ alignSelf: 'flex-start' }}
                />

                <div>
                    <Text $fontSize="14px" $medium>
                        {data.title}
                    </Text>
                    <Text $fontSize="14px" $color="gray2" $ellipsis $lineClamp={2}>
                        {data.description}
                    </Text>
                </div>
                <IconButton
                    style={{
                        width: '25px',
                        height: '25px',
                    }}
                    disabled={disabled}
                >
                    {isSelected ? <CheckCircleIcon /> : <UnCheckCircleIcon />}
                </IconButton>
            </StyledCardContent>
        </StyledCard>
    );
});

export const SelectedActivityCard = ({ data, onEdit, onDelete, padding }) => {
    const renderData = useMemo(() => {
        return processMindgymActivity(data);
    }, [data]);

    return (
        <StyledCard
            padding={padding ? padding : '15px 19px 19px 19px'}
            $backgroundColor="white"
            $borderColor="gray7"
        >
            <StyledCardContent
                style={{
                    display: 'grid',
                    gridTemplateColumns: '48px 1fr auto',
                    columnGap: '15px',
                    alignItems: 'flex-start',
                }}
            >
                <Avatar
                    alt={`activity_${renderData?.title}`}
                    src={renderData?.icon}
                    $avatarSize="48px"
                    $borderRadius="8px"
                    noBackgroundColor
                />
                <div>
                    <Text $fontSize="14px" $medium>
                        {renderData?.title}
                    </Text>
                    <Text $fontSize="12px" $color="gray2">
                        {renderData?.date}
                    </Text>
                    <Text $fontSize="12px" $color="gray2">
                        {renderData?.detail}
                    </Text>
                </div>
                <div style={{ display: 'flex', columnGap: '15px' }}>
                    {onEdit && (
                        <IconButton
                            style={{
                                width: '16px',
                                height: '16px',
                            }}
                            onClick={onEdit}
                        >
                            <EditIcon fill="black" width="16px" height="16px" />
                        </IconButton>
                    )}
                    {onDelete && (
                        <IconButton
                            style={{
                                width: '16px',
                                height: '16px',
                            }}
                            onClick={onDelete}
                        >
                            <DeleteIcon fill="black" width="16px" height="16px" />
                        </IconButton>
                    )}
                </div>
            </StyledCardContent>
        </StyledCard>
    );
};

export const DuplicateActivityDialog = ({
    open,
    onCancel,
    duplicateActivity,
    onEdit,
    onDelete,
    handleClickActivity,
}) => {
    return (
        <StyledDialog
            open={open}
            onClose={onCancel}
            aria-labelledby="Duplicate-Activity-Dialog"
            PaperComponent={StyledDuplicatePaper}
        >
            <IconButton onClick={onCancel} style={{ position: 'absolute', top: 0, right: 0 }}>
                <CrossIcon fill="black" width="20px" height="20px" />
            </IconButton>
            <Text $medium>You already have this activity in your list.</Text>

            <div style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}>
                {duplicateActivity?.map((item, index) => (
                    <SelectedActivityCard
                        key={`duplicate-activity-${index}`}
                        data={item}
                        onEdit={() => onEdit(item.id)}
                        onDelete={() => onDelete(item.id, true)}
                    />
                ))}
            </div>
            <ContainedButton
                $width="148px"
                $backgroundColor="gray6"
                style={{ padding: '5px 15px', boxShadow: 'none' }}
                onClick={() => handleClickActivity(duplicateActivity[0].type, true)}
            >
                <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px' }}>
                    <PlusIcon />
                    <Text $fontSize="14px" $color="primaryBlue">
                        Add another
                    </Text>
                </div>
            </ContainedButton>
        </StyledDialog>
    );
};

const WorkoutSelect = ({
    selectedWorkout,
    errors,
    onChange,
    workoutOptions,
    handleDeleteWorkout,
    handleCreateWorkout,
}) => {
    const [selectedGroup, setSelectedGroup] = useState({});

    useEffect(() => {
        if (_.isEmpty(workoutOptions)) return;

        if (_.isEmpty(selectedGroup)) {
            setSelectedGroup(workoutOptions[0]);
        } else {
            setSelectedGroup((prev) => workoutOptions.find((item) => item.type === prev.type));
        }
    }, [workoutOptions]);

    const onSelectWorkout = (value) => {
        let newSelectedWorkout = JSON.parse(JSON.stringify(selectedWorkout));

        const foundIndex = newSelectedWorkout.findIndex((item) => item.includes(value) );

        if (foundIndex === -1) {
            newSelectedWorkout.push(`${selectedGroup.title}#${value}`);
        } else {
            newSelectedWorkout.splice(foundIndex, 1);
        }

        console.log('newSelectedWorkout',newSelectedWorkout);

        onChange('workout', newSelectedWorkout);
    };

    const onRemoveSelectedWorkout = (index) => {
        const newSelectedWorkout = JSON.parse(JSON.stringify(selectedWorkout));
        newSelectedWorkout.splice(index, 1);

        onChange('workout', newSelectedWorkout);
    };

    /*--------------- add new workout ---------------*/
    const [inputValue, setInputValue] = useState(false);

    const onClickAdd = () => {
        setInputValue('');
    };

    const onSaveWorkout = () => {
        handleCreateWorkout(selectedGroup.type, inputValue.trim());
        setInputValue(false);
    };

    return (
        <div style={{ marginTop: '10px' }}>
            <Text $fontSize="12px" style={{ paddingLeft: '20px' }}>
                Select Workout <Span $color="pink">*</Span>
            </Text>
            <StyledWorkoutGroupContainer>
                {workoutOptions?.map((item) => (
                    <StyledWorkoutGroupButton
                        key={item.type}
                        $isActive={selectedGroup.type === item.type}
                        onClick={() => setSelectedGroup(item)}
                    >
                        {item.title}
                    </StyledWorkoutGroupButton>
                ))}
            </StyledWorkoutGroupContainer>
            <StyledWorkoutContainer>
                {!_.isEmpty(selectedGroup) &&
                    selectedGroup?.workout?.map((item, index) => (
                        <WorkoutButton
                            key={`workout-${selectedGroup.type}-${index}`}
                            data={item}
                            index={index}
                            onSelect={() => onSelectWorkout(item)}
                            onDelete={() => handleDeleteWorkout(selectedGroup.type, index)}
                            $isActive={selectedWorkout.find(ele => ele.includes(item))}
                        />
                    ))}
                {inputValue === false ? (
                    <AddWorkoutButton onClick={onClickAdd} />
                ) : (
                    <WorkoutInput
                        value={inputValue}
                        onChange={setInputValue}
                        onSave={onSaveWorkout}
                    />
                )}
            </StyledWorkoutContainer>
            <StyledSelectedWorkoutContainer
                error={errors.find((value) => value.property === 'workout')}
            >
                {selectedWorkout?.map((item, index) => (
                    <SelectedWorkoutButton
                        key={`selected-workout-${selectedGroup.type}-${index}`}
                        data={item.split("#")[1]}
                        onClick={() => onRemoveSelectedWorkout(index)}
                    />
                ))}
            </StyledSelectedWorkoutContainer>
            {errors.find((value) => value.property === 'workout') && (
                <StyledFormHelperText error>
                    {errors.find((value) => value.property === 'workout')?.message}
                </StyledFormHelperText>
            )}
        </div>
    );
};

const WorkoutButton = ({ data, onSelect, onDelete, $isActive }) => {
    return (
        <StyledWorkoutButtonContainer>
            <StyledWorkoutButton onClick={onSelect} $isActive={$isActive}>
                {data}
            </StyledWorkoutButton>
            <StyledWorkoutButtonDividerContainer>
                <StyledWorkoutButtonDivider $isActive={$isActive} />
            </StyledWorkoutButtonDividerContainer>
            <StyledWorkoutDeleteButton onClick={onDelete} $isActive={$isActive}>
                <DeleteIcon fill="black" width="14px" height="14px" />
            </StyledWorkoutDeleteButton>
        </StyledWorkoutButtonContainer>
    );
};

const SelectedWorkoutButton = ({ data, onClick }) => {
    return (
        <StyledSelectedWorkoutContainerButton>
            <Text $color="white">{data}</Text>
            <IconButton onClick={onClick}>
                <CrossIcon width="20px" height="20px" />
            </IconButton>
        </StyledSelectedWorkoutContainerButton>
    );
};

const AddWorkoutButton = ({ onClick }) => {
    return (
        <ContainedButton
            $width="91px"
            $backgroundColor="gray6"
            style={{ padding: '5px 15px', boxShadow: 'none' }}
            onClick={onClick}
        >
            <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px' }}>
                <PlusIcon />
                <Text $fontSize="14px" $color="primaryBlue">
                    Add
                </Text>
            </div>
        </ContainedButton>
    );
};

const WorkoutInput = ({ value, onChange, onSave }) => {
    const inputRef = useRef();

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    // const handleSave = () => {
    //     if (inputRef.current) {
    //         console.log('inputRef.current', inputRef.current.innerText);
    //     }
    // };

    return (
        <StyledWorkoutInput>
            <span
                ref={inputRef}
                role="textbox"
                contentEditable
                style={{ minWidth: '70px' }}
                onInput={(e) => {
                    onChange(e.currentTarget.textContent);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        onSave();
                    }
                }}
                // suppressContentEditableWarning={true}
            >
                {/* {value} */}
            </span>
            <IconButton
                onClick={onSave}
                style={{
                    width: '20px',
                    height: '20px',
                }}
            >
                <CheckIcon fill="black" width="14" height="14" />
            </IconButton>
        </StyledWorkoutInput>
    );
};

export const ActivityDialog = ({
    open,
    data,
    selectedActivity,
    inputValue,
    setInputValue,
    onCancel,
    loading,
    onSave,
    minDate,
    maxDate,
    workoutOptions,
    handleDeleteWorkout,
    handleCreateWorkout,
    dateAndPeriodData,
}) => {
    const [errors, setErrors] = useState([]);

    const onChange = (name, value) => {
        setInputValue((prev) => ({ ...prev, [name]: value }));

        setErrors((prev) => {
            let newErrors = JSON.parse(JSON.stringify(prev));
            if (['startDate', 'endDate'].includes(newErrors)) {
                newErrors = newErrors.filter(
                    (error) => error.property !== `startDate` && error.property !== `endDate`
                );
            } else {
                newErrors = newErrors.filter((error) => error.property !== name);

                if (name === 'workout') {
                    newErrors = newErrors.filter((error) => error.property !== `startDate`);
                }
            }
            return newErrors;
        });
    };

    const handleCancel = () => {
        onCancel();
        setErrors([]);
    };

    const handleSave = () => {
        console.log('handleSave', inputValue);

        const newErrors = [];

        const { type, id, startDate, endDate, workout, duration, frequency } = inputValue;

        if (!startDate) {
            newErrors.push({
                property: 'startDate',
                message: 'Please provide a Start Date.',
            });
        }
        if (!endDate) {
            newErrors.push({
                property: 'endDate',
                message: 'Please provide a End Date.',
            });
        }

        if (['MOOD_TRACKER'].includes(type)) {
            if (!frequency) {
                newErrors.push({
                    property: 'frequency',
                    message: 'Please select a Frequency.',
                });
            }
        }

        if (type === 'BEHAVIORAL_TRACKER' && _.isEmpty(workout)) {
            newErrors.push({
                property: 'workout',
                message: 'Please select workout.',
            });
        }

        if (['BREATHING_EXERCISE', 'MINDFULNESS'].includes(type) && !workout) {
            newErrors.push({
                property: 'workout',
                message: 'Please select Workout.',
            });
        }

        if (['BREATHING_EXERCISE', 'PROGRESSIVE_MUSCLE_RELAXATION', 'MINDFULNESS'].includes(type) && !duration) {
            newErrors.push({
                property: 'duration',
                message: 'Please select Duration.',
            });
        }

        if (startDate && endDate) {
            let sameTypeActivity = selectedActivity.filter(
                (item) => item.type === type && item.id !== id
            );

            if (['BREATHING_EXERCISE', 'MINDFULNESS'].includes(type)) {
                sameTypeActivity = sameTypeActivity.filter((item) => item.workout === workout);
            }

            if (!_.isEmpty(sameTypeActivity)) {
                const isOverlap = sameTypeActivity.some(
                    (item) =>
                        dayjs(startDate).isBetween(
                            dayjs(item.startDate),
                            dayjs(item.endDate),
                            'date',
                            '[]'
                        ) ||
                        dayjs(endDate).isBetween(
                            dayjs(item.startDate),
                            dayjs(item.endDate),
                            'date',
                            '[]'
                        )
                );

                if (isOverlap) {
                    newErrors.push({
                        property: 'startDate',
                        message: 'Start Date and End Date cannot overlap.',
                    });
                }
            }
        }

        if (
            !_.isEmpty(newErrors) ||
            !dayjs(startDate).isValid() ||
            !dayjs(endDate).isValid() ||
            !dayjs(startDate).isBetween(
                dayjs(dateAndPeriodData.startDate),
                dayjs(dateAndPeriodData.endDate),
                'day',
                '[]'
            ) ||
            !dayjs(endDate).isBetween(
                dayjs(dateAndPeriodData.startDate),
                dayjs(dateAndPeriodData.endDate),
                'day',
                '[]'
            )
        ) {
            return setErrors(newErrors);
        }

        onSave(inputValue);
        handleCancel();
    };

    return (
        <StyledDialog
            open={open}
            onClose={handleCancel}
            aria-labelledby="Activity-Dialog"
            PaperComponent={StyledPaper}
        >
            <div
                style={{
                    padding: '23px',
                    display: 'grid',
                    gridTemplateColumns: '64px auto',
                    columnGap: '15px',
                    alignItems: 'flex-start',
                }}
            >
                <Avatar
                    alt={`activity_${data?.title}`}
                    src={data?.icon}
                    $avatarSize="64px"
                    $borderRadius="12px"
                />
                <div>
                    <Text $fontSize="14px" $medium>
                        {data?.title}
                    </Text>

                    <StyledShowMoreContainer>
                        <ShowMore
                            lines={3}
                            more="See more"
                            less="See lesss"
                            anchorClass="showMoreButton"
                        >
                            {data?.description}
                        </ShowMore>
                    </StyledShowMoreContainer>
                </div>
            </div>
            <HorizontalRule />
            <StyledDialogContent>
                <Text $fontSize="16px" $medium>
                    Set Dates & Period
                </Text>
                <StyledSelectContainer style={{ marginTop: '10px' }}>
                    <KeyboardDatePicker
                        id="startDate"
                        name="startDate"
                        label={
                            <Span $color="black">
                                Start Date <Span $color="pink">*</Span>
                            </Span>
                        }
                        minDate={minDate ? dayjs(minDate) : undefined}
                        maxDate={
                            inputValue?.endDate
                                ? dayjs(inputValue?.endDate)
                                : maxDate
                                ? dayjs(maxDate)
                                : undefined
                        }
                        value={inputValue?.startDate}
                        onChange={(date) => onChange('startDate', date)}
                        disabled={loading}
                        helperText={errors.find((value) => value.property === 'startDate')?.message}
                    />
                    <KeyboardDatePicker
                        id="endDate"
                        name="endDate"
                        label={
                            <Span $color="black">
                                End Date <Span $color="pink">*</Span>
                            </Span>
                        }
                        minDate={
                            inputValue?.startDate
                                ? dayjs(inputValue?.startDate)
                                : minDate
                                ? dayjs(minDate)
                                : undefined
                        }
                        maxDate={maxDate ? dayjs(maxDate) : undefined}
                        value={inputValue?.endDate}
                        onChange={(date) => onChange('endDate', date)}
                        disabled={loading}
                        helperText={errors.find((value) => value.property === 'endDate')?.message}
                    />
                </StyledSelectContainer>
                {!_.isEmpty(data?.selectInput) && (
                    <StyledSelectContainer>
                        {data?.selectInput?.map((item) => (
                            <Select
                                key={item.name}
                                id={item.name}
                                label={item.label}
                                required
                                name={item.name}
                                value={inputValue[item.name]}
                                onChange={(e) => onChange(item.name, e.target.value)}
                                options={item.options}
                                placeholder={String(item.placeholder)}
                                helperText={
                                    errors.find((value) => value.property === item.name)?.message
                                }
                            />
                        ))}
                    </StyledSelectContainer>
                )}
                {data?.workoutInput && (
                    <WorkoutSelect
                        selectedWorkout={inputValue?.workout}
                        errors={errors}
                        onChange={onChange}
                        workoutOptions={workoutOptions}
                        handleDeleteWorkout={handleDeleteWorkout}
                        handleCreateWorkout={handleCreateWorkout}
                    />
                )}
            </StyledDialogContent>
            <StyledDialogActions>
                <OutlinedButton $width="96px" onClick={handleCancel}>
                    Cancel
                </OutlinedButton>
                <ContainedButton $width="96px" onClick={handleSave}>
                    Save
                </ContainedButton>
            </StyledDialogActions>
        </StyledDialog>
    );
};

const ActivityItem = ({ data }) => {
    const renderData = useMemo(() => {
        return processMindgymActivity(data);
    }, [data]);

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '48px 1fr auto',
                columnGap: '15px',
                alignItems: 'flex-start',
                marginBottom: '15px',
            }}
        >
            <Avatar
                alt={`activity_${renderData?.title}`}
                src={renderData?.icon}
                $avatarSize="48px"
                $borderRadius="8px"
            />
            <div>
                <Text $fontSize="14px" $medium>
                    {renderData?.title}
                </Text>
                <Text $fontSize="12px" $color="gray2">
                    {renderData?.date}
                </Text>
                <Text $fontSize="12px" $color="gray2">
                    {renderData?.detail}
                </Text>
            </div>
            <div>
                {renderData?.status === 'incomplete' ? (
                    <Tag $backgroundColor="pink" style={{ width: '110px' }}>
                        <Text $fontSize="14px" $color="white">
                            Incomplete
                        </Text>
                    </Tag>
                ) : (
                    <Tag $fontSize="14px" $color="white" style={{ width: '110px' }}>
                        <Text $fontSize="14px" $color="white">
                            Completed
                        </Text>
                    </Tag>
                )}
            </div>
        </div>
    );
};

export const HomeworkProgress = ({ data }) => {
    return (
        <StyledCard
            style={{
                padding: '15px 20px 20px 20px',
            }}
        >
            <StyledCardContent>
                <Text $fontSize="18px" $bold>
                    Friday Homework Progress
                </Text>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingRight: '35px',
                        marginTop: '20px',
                        marginBottom: '15px',
                    }}
                >
                    <Text $fontSize="16px" $bold>
                        Activity
                    </Text>
                    <Text $fontSize="16px" $bold>
                        Status
                    </Text>
                </div>
                <div>
                    {data.map((item, index) => (
                        <Fragment key={`activity-${item.type}`}>
                            <ActivityItem data={item} />
                            {index + 1 !== data.length && (
                                <HorizontalRule $color="gray6" style={{ marginBottom: '15px' }} />
                            )}
                        </Fragment>
                    ))}
                </div>
            </StyledCardContent>
        </StyledCard>
    );
};

export const MindgymAppCard = ({ mdUp }) => {
    return (
        <div
            style={{
                marginTop: mdUp ? '40px' : '24px',
                backgroundColor: '#789EFF',
                maxWidth: '600px',
                borderRadius: '16px',
                padding: mdUp ? '20px 35px' : '30px 0 30px 15px',
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'space-between',
            }}
        >
            <div>
                <div
                    style={{
                        display: 'flex',
                        width: '100%',
                        overflow: 'visible',
                    }}
                >
                    <div
                        style={{
                            width: mdUp ? 'auto' : 'max-content',
                        }}
                    >
                        {mdUp ? (
                            <Text
                                $color="white"
                                $fontSize="32px"
                                style={{
                                    fontWeight: 700,
                                    fontFamily: 'Poppins',
                                }}
                            >
                                Download application
                            </Text>
                        ) : (
                            <Fragment>
                                <Text
                                    $color="white"
                                    style={{
                                        fontWeight: 600,
                                        fontFamily: 'Poppins',
                                    }}
                                >
                                    Download
                                </Text>
                                <Text
                                    $color="white"
                                    $fontSize="28px"
                                    style={{
                                        fontWeight: 700,
                                        fontFamily: 'Poppins',
                                    }}
                                >
                                    application
                                </Text>
                            </Fragment>
                        )}
                        <Text
                            $color="white"
                            $fontSize={mdUp ? '16px' : '12px'}
                            style={{
                                fontWeight: 500,
                                fontFamily: 'Poppins',
                            }}
                        >
                            to complete your homework.
                        </Text>
                    </div>
                    <div
                        style={{
                            position: 'relative',
                        }}
                    >
                        {!mdUp && (
                            <div
                                style={{
                                    width: '138px',
                                    height: '120px',
                                    position: 'absolute',
                                    top: '-20px',
                                }}
                            >
                                <img
                                    src={MindGymIcon}
                                    alt="mindgyn-icon"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div style={{ marginTop: '15px' }}>
                    <a
                        href={MINDGYM_APPLE_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginRight: '10px' }}
                    >
                        <img src={AppStoreBadge} alt="app-store-badge" />
                    </a>
                    <a href={MINDGYM_PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                        <img src={PlayStoreBadge} alt="play-store-badge" />
                    </a>
                </div>
            </div>

            {mdUp && (
                <div
                    style={{
                        width: '150px',
                        height: '130px',
                        position: 'static',
                    }}
                >
                    <img src={MindGymIcon} alt="mindgyn-icon" style={{ width: '100%' }} />
                </div>
            )}
        </div>
    );
};
